import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  TextField,
  Pagination,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  DeleteForever as DeleteForeverIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { employeeAPI } from '../services/api';
import ExcelImport from './ExcelImport';
import { Employee, EmployeeSearchFilter } from '../types/Employee';

const DeletedEmployeesList: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<EmployeeSearchFilter>({
    nome: '',
    cognome: '',
    matricola: '',
    codice_fiscale: '',
    dipartimento: '',
    stato: '',
    ente: '',
    regione_sede: '',
    tipo_sede: '',
    ruolo: '',
    page: 1,
    limit: 50,
  });
  const [departments, setDepartments] = useState<string[]>([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadDepartments();
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [filter, page]);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        page: page.toString(),
        limit: filter.limit.toString(),
      };

      if (filter.nome) params.nome = filter.nome;
      if (filter.cognome) params.cognome = filter.cognome;
      if (filter.codice_fiscale) params.codice_fiscale = filter.codice_fiscale;
      if (filter.dipartimento) params.dipartimento = filter.dipartimento;

      const data = await employeeAPI.getDeletedEmployees(params);

      setEmployees(data.employees || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      // Errore('Errore caricamento dipendenti eliminati:', err);
      setError(err.response?.data?.error || err.message || 'Errore nel caricamento dei dipendenti eliminati');
    } finally {
      setLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const depts = await employeeAPI.getDepartments();
      setDepartments(depts.departments || []);
    } catch (err) {
      // Errore('Errore caricamento dipartimenti:', err);
    }
  };

  const handleFilterChange = (field: keyof EmployeeSearchFilter, value: any) => {
    setFilter(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    setFilter(prev => ({ ...prev, page: newPage }));
  };

  const handleRestoreClick = (employee: Employee) => {
    setSelectedEmployee(employee);
    setRestoreDialogOpen(true);
  };

  const handleRestore = async () => {
    if (!selectedEmployee) return;

    try {
      setLoading(true);
      await employeeAPI.restoreEmployee(selectedEmployee.id);

      setRestoreDialogOpen(false);
      setSelectedEmployee(null);
      loadEmployees();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore nel ripristino del dipendente');
    } finally {
      setLoading(false);
    }
  };

  const handlePermanentDeleteClick = (employee: Employee) => {
    setSelectedEmployee(employee);
    setDeleteDialogOpen(true);
  };

  const handlePermanentDelete = async () => {
    if (!selectedEmployee) return;

    try {
      setIsDeleting(true);
      await employeeAPI.permanentDeleteEmployee(selectedEmployee.id);

      setDeleteDialogOpen(false);
      setSelectedEmployee(null);
      loadEmployees();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore nell\'eliminazione definitiva del dipendente');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleImportSuccess = () => {
    setImportDialogOpen(false);
    loadEmployees();
  };

  if (loading && employees.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0, m: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2} sx={{ borderBottom: '1px solid #e0e0e0' }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Dipendenti Eliminati
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Gestisci i dipendenti eliminati: ripristina o elimina definitivamente
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => setImportDialogOpen(true)}
        >
          Importa Excel
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 0, m: 0 }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <Box display="flex" gap={1} flexWrap="wrap">
            <TextField
              label="Nome"
              value={filter.nome}
              onChange={(e) => handleFilterChange('nome', e.target.value)}
              size="small"
              sx={{ minWidth: 120 }}
            />
            <TextField
              label="Cognome"
              value={filter.cognome}
              onChange={(e) => handleFilterChange('cognome', e.target.value)}
              size="small"
              sx={{ minWidth: 120 }}
            />
            <TextField
              label="CF"
              value={filter.codice_fiscale}
              onChange={(e) => handleFilterChange('codice_fiscale', e.target.value)}
              size="small"
              sx={{ minWidth: 120 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Dipartimento</InputLabel>
              <Select
                value={filter.dipartimento}
                onChange={(e) => handleFilterChange('dipartimento', e.target.value)}
                label="Dipartimento"
              >
                <MenuItem value="">Tutti</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        
        <Box sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <TableContainer sx={{ maxHeight: '60vh', overflow: 'auto', width: '100%' }}>
            <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'auto' }}>
                            <TableHead>
                 <TableRow sx={{ backgroundColor: '#E3F2FD' }}>
                   <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Cognome e Nome</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Ruolo</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Sede</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Codice Sede</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Tipo Sede</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Regione</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Indirizzo</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Istituzione</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>PEC</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Email Sede</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Telefono</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Ente</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Referente</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Assunzione</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Qualifica</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Livello</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Email</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Cellulare</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Ore Sett.</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Giorni Sett.</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Lun</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Mar</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Mer</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Gio</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Ven</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Riassunzione</TableCell>
                  <TableCell sx={{ color: '#0d47a1', fontWeight: 'bold', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={27} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Nessun dipendente eliminato trovato</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((employee) => (
                    <TableRow key={employee.id} hover>
                      <TableCell>{employee.cognome} {employee.nome}</TableCell>
                      <TableCell>{employee.ruolo}</TableCell>
                      <TableCell>{employee.sede}</TableCell>
                      <TableCell>{employee.codice_sede}</TableCell>
                      <TableCell>{employee.tipo_sede}</TableCell>
                      <TableCell>{employee.regione_sede}</TableCell>
                      <TableCell>{employee.indirizzo_sede}</TableCell>
                      <TableCell>{employee.istituzione_sede}</TableCell>
                      <TableCell>{employee.pec_sede}</TableCell>
                      <TableCell>{employee.email_sede}</TableCell>
                      <TableCell>{employee.telefono_sede}</TableCell>
                      <TableCell>{employee.ente}</TableCell>
                      <TableCell>{employee.referente}</TableCell>
                      <TableCell>{employee.data_assunzione instanceof Date ? employee.data_assunzione.toLocaleDateString('it-IT') : employee.data_assunzione}</TableCell>
                      <TableCell>{employee.qualifica}</TableCell>
                      <TableCell>{employee.livello}</TableCell>
                      <TableCell>{employee.email}</TableCell>
                      <TableCell>{employee.telefono || employee.cellulare}</TableCell>
                      <TableCell>{employee.ore_settimanali}</TableCell>
                      <TableCell>{employee.giorni_settimanali}</TableCell>
                      <TableCell>{employee.orario_lunedi}</TableCell>
                      <TableCell>{employee.orario_martedi}</TableCell>
                      <TableCell>{employee.orario_mercoledi}</TableCell>
                      <TableCell>{employee.orario_giovedi}</TableCell>
                      <TableCell>{employee.orario_venerdi}</TableCell>
                      <TableCell>{employee.riassunzione}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5}>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleRestoreClick(employee)}
                            title="Ripristina"
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handlePermanentDeleteClick(employee)}
                            title="Elimina definitivamente"
                          >
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {total > 0 && (
            <Box display="flex" justifyContent="center" p={2}>
              <Pagination
                count={Math.ceil(total / filter.limit)}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Dialog Ripristino */}
      <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)}>
        <DialogTitle>Ripristina Dipendente</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler ripristinare {selectedEmployee?.nome} {selectedEmployee?.cognome}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Il dipendente tornerà attivo e potrà essere visualizzato nell'elenco principale.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)}>Annulla</Button>
          <Button onClick={handleRestore} variant="contained" color="success">
            Ripristina
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Eliminazione Definitiva */}
      <Dialog open={deleteDialogOpen} onClose={() => !isDeleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Eliminazione Definitiva</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>ATTENZIONE!</strong> Questa operazione è irreversibile.
          </Alert>
          <Typography>
            Sei sicuro di voler eliminare definitivamente {selectedEmployee?.nome} {selectedEmployee?.cognome}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Tutti i dati del dipendente, inclusi presenze e storico, saranno cancellati permanentemente dal database.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Annulla
          </Button>
          <Button
            onClick={handlePermanentDelete}
            variant="contained"
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? 'Eliminazione...' : 'Elimina Definitivamente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Import Excel */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Importa Dipendenti da Excel</DialogTitle>
        <DialogContent>
          <ExcelImport onSuccess={handleImportSuccess} onClose={() => setImportDialogOpen(false)} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DeletedEmployeesList;
